import HomeOutlined from "@ant-design/icons/HomeOutlined";

import { Button, Form, Input, Layout, message, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { PasswordStrengthIndicators } from "components/modules/user-management/users/components/my-profile/components/password-strength-indicator";
import { push } from "connected-react-router";
import QueueAnim from "rc-queue-anim";
import "rc-texty/assets/index.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import OtpInput from "react-otp-input";
import { useStore } from "react-redux";
import { useUrlQuery } from "utils/hooks/use-url-query";
import { useSDKActionWithDeps } from "../../../@sdk/sdk.hooks";
import "./reset-password.page.style.scss";

const { Footer } = Layout;
const { Title } = Typography;

const ResetPasswordPage = () => {
  const store = useStore();

  const goToDashboard = useCallback(() => store.dispatch(push("/")), [store]);
  const goToLogin = useCallback(() => store.dispatch(push("/login")), [store]);
  const {
    doAction: onResetPasswordRequest,
    isProcessing,
    response: otpRequestResponse,
  } = useSDKActionWithDeps(
    () => ({
      action: (SDK) => (values: { email: string; domain; string }) =>
        SDK.emailResetPasswordLink({
          domain: values.domain.toLowerCase(),
          email: values.email.toLowerCase(),
        }).then((res) => {
          setStep(2);
        }),
      successMessage: "One time secret has been emailed to you",
      failureMessage: "Something went wrong",
    }),
    [],
  );

  const domainConfig = useMemo(() => {
    if (window.location.href.includes(".clickconnector.io")) {
      return {
        domain: window.location.href
          .split(".clickconnector.io")[0]
          .replace("https://", ""),
        editable: false,
      };
    }
    return {
      domain: "",
      editable: true,
    };
  }, []);

  const urlQuery = useUrlQuery();
  const secret = urlQuery.get("secret");
  const domain = domainConfig.domain || urlQuery.get("domain");
  const email = urlQuery.get("email");

  const {
    doAction: resetPasswordWithCode,
    isProcessing: isResetProcessing,
    response: resetPasswordResponse,
    dispatch,
  } = useSDKActionWithDeps(
    () => ({
      action:
        (SDK) =>
        (values: {
          domain: string;
          email: string;
          secret: string;
          password: string;
        }) => {
          return SDK.resetPasswordWithCode({
            domain: values.domain.toLowerCase(),
            email: values.email.toLowerCase(),
            secret: values.secret,
            newPassword: values.password,
          }).then((res) => {
            const enableSubDomains =
              !window.location.href.includes("localhost") &&
              !window.location.href.includes("vercel.app");

            if (enableSubDomains) {
              window.location.href = `https://${values.domain.toLowerCase()}.clickconnector.io`;
            } else {
              goToLogin();
            }
          });
        },
      successMessage: "Password has been reset",
      failureMessage: "Something went wrong",
    }),
    [goToLogin],
  );

  const [formData, setFormData] = useState({} as any);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);

  const [resetRequestForm] = useForm();
  const [resetForm] = useForm();

  useEffect(() => {
    if (step === 1 && domain && email && secret) {
      const formValues = {
        domain,
        email,
      };
      resetRequestForm.setFieldsValue(formValues);
      setFormData(formValues);
      setOtp(secret!);
      setStep(2);
    }
  }, [domain, email, resetRequestForm, secret, step]);

  return (
    <Layout className="layout h-full w-full flex flex-col justify-center items-center reset-password-page">
      <img
        src="/cc-assets/logo.png"
        style={{ width: 240 }}
        // onClick={() => {
        //   setStep(step === 2 ? 1 : step + 1);
        // }}
        alt="ClickConnector"
      />

      <Flipper flipKey={step}>
        <Flipped flipId={"1"}>
          <div
            className="mt-8 bg-white dark:bg-gray-900 p-12 rounded-md  mode_transition"
            style={step === 1 ? { height: 300 } : { height: 500, width: 600 }}
          >
            <Flipped inverseFlipId={"1"} scale>
              {step === 1 ? (
                <div>
                  <Title level={3}>Reset Password</Title>

                  <Form
                    name="normal_login"
                    className="login-form"
                    preserve={true}
                    onFinish={() => {
                      const formValues = resetRequestForm.getFieldsValue();
                      onResetPasswordRequest(formValues);
                      setFormData(formValues);
                    }}
                    form={resetRequestForm}
                    initialValues={{
                      domain: domainConfig.domain || "",
                    }}
                  >
                    <QueueAnim>
                      <Form.Item
                        key="domain"
                        name="domain"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your workspace domain",
                          },
                        ]}
                      >
                        <Input
                          prefix={
                            <HomeOutlined className="site-form-item-icon" />
                          }
                          placeholder="Workspace Domain"
                          size="large"
                          disabled={!domainConfig.editable}
                        />
                      </Form.Item>

                      <Form.Item
                        key="email"
                        name="email"
                        rules={[
                          {
                            required: true,
                            message: "Please input your Email!",
                          },
                        ]}
                      >
                        <Input
                          prefix={<i className="ri-user-line"></i>}
                          placeholder="Email"
                          size="large"
                        />
                      </Form.Item>
                      <Form.Item key="submit">
                        <Button
                          type="primary"
                          htmlType="submit"
                          className="login-form-button"
                          loading={isProcessing}
                          size="large"
                        >
                          Reset Password
                        </Button>
                        Or{" "}
                        <Button type="link" onClick={() => goToLogin()}>
                          Login Now
                        </Button>
                      </Form.Item>
                    </QueueAnim>
                  </Form>
                </div>
              ) : (
                <div>
                  <Title level={3}>Enter your OTP</Title>
                  <QueueAnim delay={400}>
                    <div key="otp" className="my-4">
                      <OtpInput
                        className="otp-input"
                        value={otp}
                        onChange={setOtp}
                        numInputs={6}
                        separator={<span>-</span>}
                      />
                    </div>
                    <div key="reset-form">
                      <Title level={3}>Enter your New Password</Title>
                      <Form
                        name="reset-password-form"
                        // className="login-form"
                        layout="vertical"
                        preserve={true}
                        onFinish={async () => {
                          try {
                            await resetForm.validateFields();
                            resetPasswordWithCode({
                              ...formData,
                              ...resetForm.getFieldsValue(),
                              secret: otp,
                            });
                          } catch (e) {
                            message.error("Please check your input");
                          }
                        }}
                        form={resetForm}
                      >
                        <Form.Item
                          key="password"
                          name="password"
                          label="Password"
                          rules={[
                            {
                              required: true,
                              message: "Please input your password!",
                            },
                          ]}
                          // hasFeedback
                        >
                          <Input.Password placeholder="Password" size="large" />
                        </Form.Item>

                        <PasswordStrengthIndicators />

                        <Form.Item
                          key="confirm-password"
                          name="confirm"
                          label="Confirm Password"
                          dependencies={["password"]}
                          hasFeedback
                          rules={[
                            {
                              required: true,
                              message: "Please confirm your password!",
                            },
                            ({ getFieldValue }) => ({
                              validator(rule, value) {
                                if (
                                  !value ||
                                  getFieldValue("password") === value
                                ) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(
                                  "The two passwords that you entered do not match!",
                                );
                              },
                            }),
                          ]}
                        >
                          <Input.Password
                            placeholder="Confirm Password"
                            size="large"
                          />
                        </Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          key="button"
                          className="login-form-button"
                          loading={isResetProcessing}
                          disabled={otp.length !== 6}
                          size="large"
                        >
                          Reset Password
                        </Button>
                      </Form>
                    </div>
                  </QueueAnim>
                </div>
              )}
            </Flipped>
          </div>
        </Flipped>
      </Flipper>

      {}

      <Footer style={{ textAlign: "center" }}>
        ClickConnector ©{new Date().getFullYear()}
      </Footer>
    </Layout>
  );
};

export default ResetPasswordPage;
